/**
 * [CCI-18182] Diff-based segmenting — eligibility check.
 *
 * Whether the Post Stream Review ("Review moment") splits a top-level change into
 * finer diff-level sub-segments (`diffSegments`) instead of segmenting at the
 * top-level node boundary only.
 *
 * This requires the M1 master experiment and the `isDiffBasedSegmentingEnabled`
 * parameter on `platform_editor_ai_new_aifc_editor_experience` (replacing the former
 * standalone `platform_editor_ai_diff_based_segmenting` feature gate). This keeps
 * segmentation scoped to the same eligibility as the whole New AIFC experience.
 */
import { expValNoExposure } from '@atlaskit/tmp-editor-statsig/expVal';

import { isStreamingUxExperienceM1EnabledNoExposure } from './streaming-ux-experience-m1-enabled';

/**
 * Returns true when diff-based segmenting is enabled for the current user.
 *
 * Reads the `isDiffBasedSegmentingEnabled` param off the
 * `platform_editor_ai_new_aifc_editor_experience` experiment, defaulting to
 * `false`. This is the **non-exposure-logging** variant: every call site lives on
 * the Review-moment render / read path (segment computation, display flattening,
 * the single-segment undo fast-path), so the canonical exposure is already logged
 * by the New AIFC experience gate-enforcement points. Using the no-exposure
 * variant here avoids inflating experiment exposure counts on every re-render.
 */
export function isDiffBasedSegmentingEnabled(): boolean {
	return (
		expValNoExposure(
			'platform_editor_ai_new_aifc_editor_experience',
			'isDiffBasedSegmentingEnabled',
			false,
		) && isStreamingUxExperienceM1EnabledNoExposure()
	);
}
